import { FormikState, useFormik } from 'formik';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHttpChangePolicyInitialAmount } from '../../hooks';
import { RootState } from '../../store';
import { IMenuItem } from '../Common';
import { MEMBERS_POLICIES_SCHEMA } from './membersPoliciesSchema';
import { LeaveRequestType } from '../../commonTypes/leaveRequest.types';
import { InputFormik, Dropdown, Button } from '../Common';
import { OrganizationMembersWithBranchDataDto } from '../../commonTypes/acs';
import { useTranslation } from '../../hooks/useTranslation';

interface IMemberPolicies {
    selectedMember: OrganizationMembersWithBranchDataDto;
    close: () => void;
    setData: Dispatch<SetStateAction<OrganizationMembersWithBranchDataDto[]>>;
}

interface IMemberPoliciesState {
    policy: IMenuItem;
    totalAmount: number;
}

const MemberPolicies = ({ selectedMember, close, setData }: IMemberPolicies) => {
    const { t } = useTranslation();
    const content = useSelector((state: RootState) => state.language.data);
    const { exec: changePolicyInitialAmount } = useHttpChangePolicyInitialAmount(setData);

    const vacationIndex = useMemo(() => {
        const index = selectedMember.policies.findIndex(policy => policy.leaveType === LeaveRequestType.VACATION);
        return index !== -1 ? index : 0;
    }, [selectedMember]);

    const INITIAL_MEMBER_POLICY_STATE: IMemberPoliciesState = {
        policy: {
            label: selectedMember.policies[vacationIndex].leaveType,
            value: selectedMember.policies[vacationIndex].leaveType,
        },
        totalAmount: selectedMember.policies[vacationIndex].initialAmount,
    };

    const areInitialValuesChanged = (memberPolicy: IMemberPoliciesState) => {
        const policy = selectedMember.policies.find(policy => policy.leaveType === memberPolicy.policy.value);

        if (!policy) return false;

        return memberPolicy.totalAmount === policy.initialAmount;
    };

    const handleSubmit = (values: IMemberPoliciesState, resetForm: (nextState?: Partial<FormikState<IMemberPoliciesState>>) => void) => {
        const originalPolicy = selectedMember.policies.find(policy => policy.leaveType === values.policy.value);
        if (!originalPolicy) return;
        changePolicyInitialAmount(selectedMember.userSub, originalPolicy.leaveType, values.totalAmount);
        resetForm();
        close();
    };

    const context = useFormik<IMemberPoliciesState>({
        initialValues: INITIAL_MEMBER_POLICY_STATE,
        validationSchema: MEMBERS_POLICIES_SCHEMA(t),
        onSubmit: (values, { resetForm }) => {
            handleSubmit(values, resetForm);
        },
    });

    return (
        <div className="member-policies">
            <h3 className="member-policies__header">{content.members.editPolicy}</h3>
            <div className="member-policies__info">
                <img className="member-policies__user-img" src={selectedMember.imageUrl} alt="" />
                <div>
                    <p className="paragraph-4">{`${selectedMember.familyName} ${selectedMember.givenName}`}</p>
                    <p className="paragraph-4">{selectedMember.email}</p>
                </div>
            </div>
            <form onSubmit={context.handleSubmit}>
                <div className="member-policies__fields">
                    <Dropdown
                        label={content.members.choosePolicy}
                        id="policy"
                        menuItems={selectedMember.policies.map(policy => ({ label: policy.leaveType, value: policy.leaveType }))}
                        selected={context.values.policy}
                        onChange={async item => {
                            await context.setFieldValue('policy', item);
                            const policy = selectedMember.policies.find(policy => policy.leaveType === item.value);
                            if (policy) await context.setFieldValue('totalAmount', policy.initialAmount);
                        }}
                        context={context}
                    />
                    <InputFormik id="totalAmount" label={content.members.totalAmount} type="number" context={context} />
                </div>
                <div className="member-policies__buttons">
                    <Button
                        text={content.common.cancel}
                        variant="text"
                        color="blue"
                        onClick={() => {
                            context.resetForm();
                            close();
                        }}
                    />
                    <Button disabled={areInitialValuesChanged(context.values)} text={content.common.submit} isSubmit />
                </div>
            </form>
        </div>
    );
};

export default MemberPolicies;
